/**
 * src/components/marketing/ViMarketing.js
 *
 * VISINVOKE MARKETING PRIMITIVES.
 *
 * A kit of branded building blocks for product detail pages. Every product page
 * imports from here, so typography, radii, borders, spacing, pill language,
 * content width and responsive behaviour are identical across the Dashboard.
 *
 * What this file deliberately does NOT do is define a page. There is no
 * "ProductPage" template here and no fixed section order. Each product composes
 * its own story from these primitives - different hero, different section
 * shapes, different rhythm - so twelve products read as twelve products rather
 * than one template with the nouns swapped.
 *
 * Constraints followed: no optional chaining / nullish operators (project rule);
 * inline styles plus one injected stylesheet for grids and breakpoints, matching
 * the rest of the codebase.
 */
import React, { useEffect } from 'react';
import { hexA } from '../../RegistryComponents';

// ---------------------------------------------------------------------------
// Tokens. The brand constants every product shares.
// ---------------------------------------------------------------------------
export const VI = {
  bg: '#0d1117',
  ink: '#ffffff',
  body: 'rgba(255,255,255,0.82)',
  muted: 'rgba(255,255,255,0.62)',
  faint: 'rgba(255,255,255,0.44)',
  line: 'rgba(255,255,255,0.10)',
  lineHi: 'rgba(255,255,255,0.16)',
  panel: 'rgba(255,255,255,0.035)',
  panelHi: 'rgba(255,255,255,0.055)',
  font: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  r: { sm: 4, md: 4, lg: 4, xl: 4, pill: 3 },
  good: '#34d399',
  warn: '#fbbf24',
  bad: '#f87171',
  info: '#7dd3fc',
};

const CSS = `
  .vim-wrap, .vim-wrap-mid, .vim-wrap-slim { width: min(calc(100vw - 56px), 1180px); margin: 0 auto; box-sizing: border-box; }
  @media (max-width: 900px) {
    .vim-wrap, .vim-wrap-mid, .vim-wrap-slim { width: calc(100vw - 30px); }
  }

  /* One grid, one set of breakpoints, used by every product. */
  .vim-g { display: grid; gap: 18px; align-items: stretch; }
  .vim-g[data-c="2"] { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .vim-g[data-c="3"] { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .vim-g[data-c="4"] { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .vim-g[data-c="5"] { grid-template-columns: repeat(5, minmax(0, 1fr)); }
  .vim-g[data-c="6"] { grid-template-columns: repeat(6, minmax(0, 1fr)); }
  @media (max-width: 1599px) { .vim-g[data-c="5"], .vim-g[data-c="6"] { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
  @media (max-width: 1339px) { .vim-g[data-c="4"], .vim-g[data-c="5"], .vim-g[data-c="6"] { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
  @media (max-width: 1080px) { .vim-g[data-c="3"], .vim-g[data-c="4"], .vim-g[data-c="5"], .vim-g[data-c="6"] { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 760px)  { .vim-g { grid-template-columns: 1fr !important; gap: 14px; } }

  /* Asymmetric layouts. */
  .vim-split      { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 56px; align-items: center; }
  .vim-split-wide { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 56px; align-items: center; }
  .vim-split-lead { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr); gap: 24px 64px; align-items: end; }
  @media (max-width: 1080px) {
    .vim-split, .vim-split-wide, .vim-split-lead { grid-template-columns: 1fr; gap: 24px; }
  }

  .vim-mosaic { display: grid; grid-template-columns: minmax(0, 1.15fr) repeat(3, minmax(0, 1fr)); gap: 18px; }
  @media (max-width: 1599px) { .vim-mosaic { grid-template-columns: minmax(0, 1.2fr) repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 1180px) { .vim-mosaic { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 760px)  { .vim-mosaic { grid-template-columns: 1fr; } }
  .vim-mosaic-feature { grid-row: span 2; }
  @media (max-width: 1180px) { .vim-mosaic-feature { grid-row: auto; grid-column: 1 / -1; } }

  /* Flow rail: steps joined by connectors rather than floating boxes. */
  .vim-flow { display: flex; align-items: stretch; gap: 0; flex-wrap: nowrap; }
  .vim-flow-step { flex: 1 1 0; min-width: 0; }
  .vim-flow-link { flex: 0 0 26px; display: flex; align-items: center; justify-content: center; }
  @media (max-width: 1339px) {
    .vim-flow { flex-wrap: wrap; gap: 14px; }
    .vim-flow-step { flex: 1 1 260px; }
    .vim-flow-link { display: none; }
  }

  .vim-hero-split { display: flex; align-items: flex-start; justify-content: space-between; gap: 56px; }
  .vim-hero-main { flex: 1 1 auto; min-width: 0; }
  .vim-hero-aside { flex: 0 0 400px; }
  @media (max-width: 1180px) {
    .vim-hero-split { flex-direction: column; gap: 30px; }
    .vim-hero-aside { flex: 1 1 auto; width: 100%; }
  }

  .vim-rail { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0; }
  .vim-rail[data-n="3"] { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .vim-rail[data-n="5"] { grid-template-columns: repeat(5, minmax(0, 1fr)); }
  @media (max-width: 900px) { .vim-rail, .vim-rail[data-n="3"], .vim-rail[data-n="5"] { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

  .vim-pop { transition: transform 220ms cubic-bezier(0.22,1,0.36,1), border-color 220ms ease, box-shadow 220ms ease; }
  .vim-pop:hover { transform: translateY(-3px); }
  .vim-btn { transition: filter 180ms ease, transform 160ms cubic-bezier(0.22,1,0.36,1); }
  .vim-btn:hover:not(:disabled) { filter: brightness(1.12); transform: translateY(-2px); }
  @media (max-width: 520px) {
    .vim-btn { width: 100% !important; max-width: 100%; white-space: normal !important; flex-wrap: wrap; padding: 13px 18px !important; }
  }
  .vim-back { transition: color 160ms ease, border-color 160ms ease, background 160ms ease; }
  .vim-btn:focus-visible, .vim-back:focus-visible, .vi-more-info-pill:focus-visible {
    outline: 3px solid #7dd3fc; outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    .vim-pop, .vim-btn, .vim-back { transition: none; }
    .vim-pop:hover { transform: none; }
  }
`;

export function useViMarketingStyles() {
  useEffect(() => {
    let el = document.getElementById('vim-css');
    if (!el) {
      el = document.createElement('style');
      el.id = 'vim-css';
      document.head.appendChild(el);
    }
    el.textContent = CSS;
  }, []);
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------
export function MarketingPage({ children, background }) {
  useViMarketingStyles();
  return (
    <div style={{ minHeight: '100vh', background: background || VI.bg, fontFamily: VI.font }}>
      {children}
    </div>
  );
}

export function Wrap({ size, children, style }) {
  const cls = size === 'slim' ? 'vim-wrap-slim' : size === 'mid' ? 'vim-wrap-mid' : 'vim-wrap';
  return <div className={cls} style={style}>{children}</div>;
}

/**
 * A horizontal band. Tone controls how the band separates itself from its
 * neighbours, which is how a page gets rhythm without every section looking the
 * same.
 *   void  - page background
 *   panel - lifted, hairline top and bottom
 *   deep  - darker inset, for a change of pace
 *   wash  - accent-tinted, for a moment that should feel different
 */
export function Band({ tone, accent, pad, children, style }) {
  const padding = pad === 'tight' ? '74px 0' : pad === 'loose' ? '110px 0' : '100px 0';
  let band = { padding };
  if (tone === 'panel') {
    band = { ...band, background: 'rgba(255,255,255,0.022)', borderTop: '1px solid ' + VI.line, borderBottom: '1px solid ' + VI.line };
  } else if (tone === 'deep') {
    band = { ...band, background: 'linear-gradient(180deg, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.16) 100%)', borderTop: '1px solid rgba(0,0,0,0.4)', borderBottom: '1px solid ' + VI.line };
  } else if (tone === 'wash') {
    const a = accent || VI.info;
    band = {
      ...band,
      background: 'linear-gradient(160deg, ' + hexA(a, 0.13) + ' 0%, ' + hexA(a, 0.04) + ' 55%, rgba(255,255,255,0.015) 100%)',
      borderTop: '1px solid ' + hexA(a, 0.22),
      borderBottom: '1px solid ' + hexA(a, 0.22),
    };
  }
  return <section style={{ ...band, ...(style || {}) }}>{children}</section>;
}

// ---------------------------------------------------------------------------
// Type
// ---------------------------------------------------------------------------
export function Eyebrow({ children, accent, style }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: accent || VI.info, ...(style || {}) }}>
      {children}
    </div>
  );
}

export function H2({ children, size, serif, style }) {
  const fs = size === 'xl' ? 'clamp(28px, 2.4vw, 46px)' : size === 'sm' ? 'clamp(20px, 1.4vw, 26px)' : 'clamp(24px, 1.85vw, 34px)';
  return (
    <h2 style={{
      fontFamily: serif ? VI.serif : VI.font,
      fontSize: fs, fontWeight: 900, color: VI.ink,
      letterSpacing: serif ? '-0.3px' : '-0.7px', lineHeight: 1.14, margin: 0,
      ...(style || {}),
    }}>{children}</h2>
  );
}

export function Lead({ children, style }) {
  return <p style={{ fontSize: 16.5, color: 'rgba(255,255,255,0.76)', lineHeight: 1.68, margin: 0, maxWidth: 960, ...(style || {}) }}>{children}</p>;
}

export function Body({ children, style }) {
  return <p style={{ fontSize: 15.5, color: VI.body, lineHeight: 1.75, margin: 0, maxWidth: 900, ...(style || {}) }}>{children}</p>;
}

/**
 * Section heading. `align` changes the whole feel of a section:
 *   split  - title left, lead right (wide, editorial)
 *   left   - stacked
 *   center - centred, for moments that should feel like a chapter break
 */
export function SectionHead({ eyebrow, title, lead, align, accent, serif, size, gap }) {
  const marginBottom = gap === 'tight' ? 22 : 34;
  if (align === 'center') {
    return (
      <div style={{ textAlign: 'center', marginBottom, maxWidth: 1100, marginLeft: 'auto', marginRight: 'auto' }}>
        {eyebrow ? <Eyebrow accent={accent} style={{ marginBottom: 12 }}>{eyebrow}</Eyebrow> : null}
        <H2 size={size} serif={serif}>{title}</H2>
        {lead ? <Lead style={{ margin: '16px auto 0' }}>{lead}</Lead> : null}
      </div>
    );
  }
  if (align === 'left') {
    return (
      <div style={{ marginBottom }}>
        {eyebrow ? <Eyebrow accent={accent} style={{ marginBottom: 12 }}>{eyebrow}</Eyebrow> : null}
        <H2 size={size} serif={serif}>{title}</H2>
        {lead ? <Lead style={{ marginTop: 16 }}>{lead}</Lead> : null}
      </div>
    );
  }
  return (
    <div className="vim-split-lead" style={{ marginBottom }}>
      <div>
        {eyebrow ? <Eyebrow accent={accent} style={{ marginBottom: 12 }}>{eyebrow}</Eyebrow> : null}
        <H2 size={size} serif={serif}>{title}</H2>
      </div>
      {lead ? <Lead>{lead}</Lead> : <div />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Statements
// ---------------------------------------------------------------------------

/** A very large claim that occupies its own moment on the page. */
export function BigClaim({ kicker, children, sub, accent, align }) {
  const centered = align === 'center';
  return (
    <div style={{ textAlign: centered ? 'center' : 'left', maxWidth: centered ? 1240 : 1500, marginLeft: centered ? 'auto' : 0, marginRight: centered ? 'auto' : 0 }}>
      {kicker ? <Eyebrow accent={accent} style={{ marginBottom: 16 }}>{kicker}</Eyebrow> : null}
      <div style={{
        fontSize: 'clamp(28px, 3vw, 56px)', fontWeight: 900, color: VI.ink,
        letterSpacing: '-1.2px', lineHeight: 1.12,
      }}>{children}</div>
      {sub ? (
        <p style={{
          fontSize: 17, color: 'rgba(255,255,255,0.74)', lineHeight: 1.7,
          margin: centered ? '22px auto 0' : '22px 0 0', maxWidth: 900,
        }}>{sub}</p>
      ) : null}
    </div>
  );
}

/** A quoted product truth, set against a rule. */
export function PullStatement({ children, accent, style }) {
  return (
    <p style={{
      fontSize: 'clamp(20px, 1.5vw, 28px)', fontWeight: 700, color: VI.ink,
      lineHeight: 1.4, margin: 0, paddingLeft: 26,
      borderLeft: '3px solid ' + (accent || VI.info),
      ...(style || {}),
    }}>{children}</p>
  );
}

// ---------------------------------------------------------------------------
// Small parts
// ---------------------------------------------------------------------------
export function PillRow({ items, accent, tone, style }) {
  const solid = tone === 'solid';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, ...(style || {}) }}>
      {items.map((t, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          borderRadius: VI.r.pill,
          border: '1px solid ' + hexA(accent, solid ? 0.5 : 0.32),
          background: solid ? hexA(accent, 0.14) : 'rgba(255,255,255,0.05)',
          padding: '9px 16px', fontSize: 13, fontWeight: 800, color: VI.ink,
        }}>
          <span style={{ color: accent, fontWeight: 900, fontSize: 12 }}>{'✓'}</span>
          {t}
        </span>
      ))}
    </div>
  );
}

/** Dense tag cloud - for long factual inventories like file formats. */
export function ChipCloud({ items, accent, style }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, ...(style || {}) }}>
      {items.map((t, i) => (
        <span key={i} style={{
          borderRadius: VI.r.sm, border: '1px solid ' + hexA(accent, 0.22),
          background: hexA(accent, 0.07), padding: '6px 11px',
          fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.9)',
          letterSpacing: '0.02em', whiteSpace: 'nowrap',
        }}>{t}</span>
      ))}
    </div>
  );
}

export function BackPill({ label, onClick, accent }) {
  return (
    <button
      type="button"
      className="vim-back"
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.color = VI.ink; e.currentTarget.style.borderColor = hexA(accent, 0.7); }}
      onMouseLeave={e => { e.currentTarget.style.color = '#aab8c9'; e.currentTarget.style.borderColor = VI.lineHi; }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'rgba(255,255,255,0.05)', border: '1px solid ' + VI.lineHi,
        borderRadius: VI.r.pill, color: '#aab8c9',
        fontSize: 13, fontWeight: 800, letterSpacing: '0.03em',
        cursor: 'pointer', padding: '9px 20px', whiteSpace: 'nowrap',
      }}
    >
      {'←'} {label}
    </button>
  );
}

export function ActionButton({ children, onClick, href, accent, accent2, disabled, pill, wide }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    boxSizing: 'border-box', whiteSpace: 'nowrap', textDecoration: 'none',
    borderRadius: VI.r.pill, padding: '15px 30px',
    fontSize: 15.5, fontWeight: 800,
    width: wide ? '100%' : 'auto',
  };
  if (disabled) {
    return (
      <button type="button" className="vim-btn" disabled aria-disabled="true" style={{
        ...base, background: 'rgba(255,255,255,0.07)', color: VI.muted,
        border: '1px solid ' + VI.lineHi, cursor: 'not-allowed',
      }}>
        <span>{children}</span>
        {pill ? (
          <span style={{
            fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: VI.warn, background: 'rgba(245,158,11,0.14)',
            border: '1px solid rgba(245,158,11,0.35)', borderRadius: VI.r.pill,
            padding: '4px 10px', lineHeight: 1,
          }}>{pill}</span>
        ) : null}
      </button>
    );
  }
  const filled = {
    ...base,
    background: accent2 ? 'linear-gradient(135deg, ' + accent + ', ' + accent2 + ')' : accent,
    color: accent2 ? VI.ink : '#04121f',
    border: 'none', cursor: 'pointer',
    boxShadow: '0 10px 28px rgba(0,0,0,0.42)',
  };
  if (href) return <a className="vim-btn" href={href} style={filled}>{children}</a>;
  return <button type="button" className="vim-btn" onClick={onClick} style={filled}>{children}</button>;
}

export function StatusPill({ label, tone }) {
  const t = tone || VI.good;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
      borderRadius: VI.r.pill, border: '1px solid ' + hexA(t, 0.5),
      background: hexA(t, 0.12), padding: '6px 14px',
      fontSize: 12, fontWeight: 800, color: t, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: t, flexShrink: 0 }} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Value rails
// ---------------------------------------------------------------------------

/** Big numbers across a rule. Reads as a fact strip, not as cards. */
export function StatRail({ items, accent, serif }) {
  return (
    <div className="vim-rail" data-n={String(items.length)} style={{
      borderTop: '1px solid ' + VI.line, borderBottom: '1px solid ' + VI.line, padding: '22px 0',
    }}>
      {items.map((s, i) => (
        <div key={i} style={{ padding: '4px 0 4px 22px', borderLeft: '2px solid ' + hexA(accent, i === 0 ? 0.9 : 0.26), minWidth: 0 }}>
          <div style={{
            fontFamily: serif ? VI.serif : VI.font,
            fontSize: 'clamp(24px, 1.7vw, 34px)', fontWeight: 900, color: accent,
            lineHeight: 1.05, letterSpacing: '-0.7px',
          }}>{s.v}</div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: VI.muted, textTransform: 'uppercase', letterSpacing: '0.09em', marginTop: 7, lineHeight: 1.4 }}>{s.l}</div>
          {s.sub ? <div style={{ fontSize: 13, color: VI.faint, marginTop: 6, lineHeight: 1.5 }}>{s.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flow / process
// ---------------------------------------------------------------------------

/**
 * A joined process rail. Unlike a grid of step cards this reads as one
 * continuous journey, which is what a workflow actually is.
 */
export function FlowRail({ steps, accent, compact }) {
  return (
    <div className="vim-flow">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          {i > 0 ? (
            <div className="vim-flow-link" aria-hidden="true">
              <span style={{ color: hexA(accent, 0.55), fontSize: 18, fontWeight: 900 }}>{'›'}</span>
            </div>
          ) : null}
          <div className="vim-flow-step">
            <div style={{
              height: '100%', boxSizing: 'border-box',
              background: VI.panel, border: '1px solid ' + hexA(accent, 0.22),
              borderRadius: VI.r.md, padding: compact ? '16px 16px' : '20px 18px',
              display: 'flex', flexDirection: 'column', gap: 9,
            }}>
              <span style={{
                alignSelf: 'flex-start', borderRadius: VI.r.pill,
                border: '1px solid ' + hexA(accent, 0.45), background: hexA(accent, 0.12),
                color: accent, padding: '4px 12px',
                fontSize: 11, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', lineHeight: 1.35,
              }}>{s.n}</span>
              <div style={{ fontSize: compact ? 15 : 16.5, fontWeight: 800, color: VI.ink, lineHeight: 1.3 }}>{s.title}</div>
              {s.text ? <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.74)', lineHeight: 1.6, margin: 0 }}>{s.text}</p> : null}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feature shapes
// ---------------------------------------------------------------------------

/** Two-column feature moment. Alternate `side` down a page for rhythm. */
export function Spotlight({ eyebrow, title, children, items, accent, side, aside, wide, serif }) {
  const textCol = (
    <div style={{ minWidth: 0 }}>
      {eyebrow ? <Eyebrow accent={accent} style={{ marginBottom: 14 }}>{eyebrow}</Eyebrow> : null}
      <H2 serif={serif}>{title}</H2>
      {children ? <div style={{ marginTop: 18 }}>{children}</div> : null}
      {items ? (
        <ul style={{ listStyle: 'none', margin: '20px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
          {items.map((it, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 14.5, color: VI.body, lineHeight: 1.55 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: accent, flexShrink: 0, marginTop: 8 }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
  const asideCol = <div style={{ minWidth: 0 }}>{aside}</div>;
  return (
    <div className={wide ? 'vim-split-wide' : 'vim-split'}>
      {side === 'right' ? asideCol : textCol}
      {side === 'right' ? textCol : asideCol}
    </div>
  );
}

/** A framed surface used as a Spotlight aside - keeps visual weight balanced. */
export function Frame({ children, accent, label, style }) {
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.02) 100%)',
      border: '1px solid ' + hexA(accent, 0.3), borderRadius: VI.r.lg,
      padding: '24px 24px', boxShadow: '0 18px 50px rgba(0,0,0,0.4)',
      ...(style || {}),
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, ' + accent + ', ' + hexA(accent, 0.15) + ')' }} />
      {label ? <Eyebrow accent={accent} style={{ marginBottom: 16, fontSize: 11 }}>{label}</Eyebrow> : null}
      {children}
    </div>
  );
}

/** Uniform card grid. `variant` changes the card's character. */
export function CardGrid({ items, cols, accent, variant }) {
  return (
    <div className="vim-g" data-c={String(cols || 3)}>
      {items.map((c, i) => (
        <div key={i} className="vim-pop" style={{
          position: 'relative', overflow: 'hidden', minWidth: 0,
          background: variant === 'quiet' ? 'rgba(255,255,255,0.025)' : VI.panel,
          border: '1px solid ' + hexA(accent, variant === 'quiet' ? 0.16 : 0.26),
          borderRadius: VI.r.md, padding: '22px 20px 20px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {variant === 'bar' ? <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, ' + accent + ', ' + hexA(accent, 0.15) + ')' }} /> : null}
          {c.sub ? <div style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.13em', color: accent }}>{c.sub}</div> : null}
          <div style={{ fontSize: 17, fontWeight: 800, color: VI.ink, lineHeight: 1.3 }}>{c.title}</div>
          {c.text ? <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.78)', lineHeight: 1.62, margin: 0 }}>{c.text}</p> : null}
          {c.items ? (
            <ul style={{ listStyle: 'none', margin: '2px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {c.items.map((it, j) => (
                <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: 'rgba(255,255,255,0.84)', lineHeight: 1.5 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent, flexShrink: 0, marginTop: 7 }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** One dominant feature beside a set of smaller ones. Breaks grid monotony. */
export function Mosaic({ feature, items, accent }) {
  return (
    <div className="vim-mosaic">
      <div className="vim-mosaic-feature vim-pop" style={{
        position: 'relative', overflow: 'hidden', minWidth: 0,
        background: 'linear-gradient(160deg, ' + hexA(accent, 0.16) + ' 0%, rgba(255,255,255,0.03) 60%)',
        border: '1px solid ' + hexA(accent, 0.4), borderRadius: VI.r.lg,
        padding: '28px 26px', display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        {feature.sub ? <Eyebrow accent={accent}>{feature.sub}</Eyebrow> : null}
        <div style={{ fontSize: 'clamp(21px, 1.5vw, 27px)', fontWeight: 900, color: VI.ink, lineHeight: 1.24 }}>{feature.title}</div>
        {feature.text ? <p style={{ fontSize: 14.5, color: VI.body, lineHeight: 1.68, margin: 0 }}>{feature.text}</p> : null}
        {feature.items ? (
          <ul style={{ listStyle: 'none', margin: 'auto 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
            {feature.items.map((it, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: VI.body, lineHeight: 1.5 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0, marginTop: 7 }} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {items.map((c, i) => (
        <div key={i} className="vim-pop" style={{
          minWidth: 0, background: VI.panel, border: '1px solid ' + hexA(accent, 0.22),
          borderRadius: VI.r.md, padding: '18px 18px', display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ fontSize: 15.5, fontWeight: 800, color: VI.ink, lineHeight: 1.28 }}>{c.title}</div>
          {c.text ? <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.76)', lineHeight: 1.58, margin: 0 }}>{c.text}</p> : null}
        </div>
      ))}
    </div>
  );
}

/** Named capability groups. */
export function GroupGrid({ groups, cols, accent, hollow }) {
  return (
    <div className="vim-g" data-c={String(cols || 4)}>
      {groups.map((g, i) => (
        <div key={i} className="vim-pop" style={{
          minWidth: 0, background: VI.panel, border: '1px solid ' + hexA(accent, 0.22),
          borderRadius: VI.r.md, padding: '18px 20px 20px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: accent, lineHeight: 1.35 }}>{g.title}</span>
            {g.count ? <span style={{ fontSize: 11.5, fontWeight: 800, color: VI.faint, whiteSpace: 'nowrap' }}>{g.count}</span> : null}
          </div>
          {g.text ? <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, margin: 0 }}>{g.text}</p> : null}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {g.items.map((it, j) => (
              <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: 'rgba(255,255,255,0.86)', lineHeight: 1.5 }}>
                <span style={hollow
                  ? { width: 6, height: 6, borderRadius: '50%', flexShrink: 0, marginTop: 7, border: '1.5px solid ' + accent, boxSizing: 'border-box' }
                  : { width: 6, height: 6, borderRadius: '50%', flexShrink: 0, marginTop: 7, background: accent }} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Two contrasting columns. */
export function Compare({ left, right }) {
  const col = (c) => {
    const tone = c.tone === 'good' ? VI.good : c.tone === 'bad' ? VI.bad : c.tone === 'warn' ? VI.warn : VI.info;
    return (
      <div style={{
        background: hexA(tone, 0.055), border: '1px solid ' + hexA(tone, 0.32),
        borderRadius: VI.r.lg, padding: '24px 24px', minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.11em', color: tone }}>{c.title}</div>
        {c.text ? <p style={{ fontSize: 14.5, color: VI.body, lineHeight: 1.62, margin: 0 }}>{c.text}</p> : null}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {c.items.map((it, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, fontSize: 14.5, color: 'rgba(255,255,255,0.86)', lineHeight: 1.5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone, flexShrink: 0, marginTop: 8 }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  return <div className="vim-g" data-c="2">{col(left)}{col(right)}</div>;
}

/** Commercial tiers. */
export function TierRow({ tiers, cols, accent }) {
  return (
    <div className="vim-g" data-c={String(cols || 3)}>
      {tiers.map((t, i) => (
        <div key={i} className="vim-pop" style={{
          position: 'relative', minWidth: 0,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid ' + hexA(accent, t.recommended ? 0.6 : 0.24),
          borderRadius: VI.r.md, padding: '24px 24px',
          display: 'flex', flexDirection: 'column', gap: 11,
          boxShadow: t.recommended ? '0 14px 42px rgba(0,0,0,0.42), 0 0 0 1px ' + hexA(accent, 0.22) : '0 10px 30px rgba(0,0,0,0.32)',
        }}>
          {t.recommended ? (
            <span style={{
              position: 'absolute', top: 14, right: 14, background: accent, color: '#04121f',
              fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase',
              borderRadius: VI.r.pill, padding: '4px 11px', lineHeight: 1,
            }}>{t.flag || 'Best value'}</span>
          ) : null}
          {t.eyebrow ? <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: accent, lineHeight: 1.4, paddingRight: 96 }}>{t.eyebrow}</div> : null}
          <div style={{ fontSize: 19, fontWeight: 900, color: VI.ink, lineHeight: 1.25 }}>{t.title}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 31, fontWeight: 900, color: VI.ink, lineHeight: 1, letterSpacing: '-0.9px' }}>{t.price}</span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: VI.muted }}>{t.billing}</span>
          </div>
          {t.capacity ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, alignSelf: 'flex-start',
              borderRadius: VI.r.pill, border: '1px solid ' + hexA(accent, 0.4), padding: '6px 14px',
              fontSize: 12.5, fontWeight: 700, color: VI.ink,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent }} />
              {t.capacity}
            </div>
          ) : null}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {t.items.map((it, j) => (
              <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: 'rgba(255,255,255,0.86)', lineHeight: 1.5 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0, marginTop: 7 }} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** A moment that interrupts the page with one idea. */
export function Callout({ eyebrow, title, text, items, accent, align }) {
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      border: '1px solid ' + hexA(accent, 0.42), borderRadius: VI.r.xl,
      background: 'linear-gradient(135deg, ' + hexA(accent, 0.15) + ' 0%, ' + hexA(accent, 0.05) + ' 58%, rgba(255,255,255,0.02) 100%)',
      padding: '32px 34px 32px 38px',
    }}>
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 4, background: accent }} />
      <div className={items ? 'vim-split-wide' : ''}>
        <div style={{ minWidth: 0, textAlign: align === 'center' ? 'center' : 'left' }}>
          {eyebrow ? <Eyebrow accent={accent} style={{ marginBottom: 10 }}>{eyebrow}</Eyebrow> : null}
          <div style={{ fontSize: 'clamp(21px, 1.55vw, 30px)', fontWeight: 900, color: VI.ink, lineHeight: 1.24, marginBottom: 12 }}>{title}</div>
          <p style={{ fontSize: 15.5, color: VI.body, lineHeight: 1.72, margin: 0, maxWidth: align === 'center' ? 900 : 'none', marginLeft: align === 'center' ? 'auto' : 0, marginRight: align === 'center' ? 'auto' : 0 }}>{text}</p>
        </div>
        {items ? (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            {items.map((it, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, fontSize: 14.5, color: VI.body, lineHeight: 1.5 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0, marginTop: 8 }} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

/** Quiet, honest boundary text. Present but never competing with the product. */
export function NoteStrip({ title, children }) {
  return (
    <div style={{
      border: '1px solid ' + VI.line, borderRadius: VI.r.md,
      background: 'rgba(255,255,255,0.022)', padding: '18px 22px',
      display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 1300,
    }}>
      {title ? <div style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.13em', color: 'rgba(255,255,255,0.5)' }}>{title}</div> : null}
      <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.64)', lineHeight: 1.72, margin: 0 }}>{children}</p>
    </div>
  );
}

/** Closing action band. */
export function CtaBand({ eyebrow, title, text, accent, children, back }) {
  return (
    <Band tone="wash" accent={accent} pad="tight">
      <Wrap>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 36, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 420px' }}>
            {eyebrow ? <Eyebrow accent={accent} style={{ marginBottom: 10 }}>{eyebrow}</Eyebrow> : null}
            <div style={{ fontSize: 'clamp(21px, 1.6vw, 30px)', fontWeight: 900, color: VI.ink, lineHeight: 1.26 }}>{title}</div>
            {text ? <p style={{ fontSize: 15.5, color: 'rgba(255,255,255,0.78)', lineHeight: 1.68, margin: '12px 0 0', maxWidth: 860 }}>{text}</p> : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {children}
            {back}
          </div>
        </div>
      </Wrap>
    </Band>
  );
}
